/* Market depth ladder.
 *
 * A depth ladder is the panel a trader sizes an order from, which makes it the
 * one where a fabricated number does the most damage. So the provenance is not
 * a footnote here — it is the first thing the panel says.
 *
 * When the API reports `depth_source: "DERIVED"` the levels below the touch
 * were CONSTRUCTED from the quoted spread. They are drawn in a visibly
 * different treatment (hatched bars, muted quantities, an explicit banner)
 * from real exchange depth, and the totals are labelled as not tradeable.
 */
import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { Panel } from '@/components/Panel'
import { useStore } from '@/store'
import { fmtPrice } from '@/lib/format'
import { Empty } from '@/components/viz'
import type { MarketDepth as Depth, DepthRow } from '@/types'

const POLL_MS = 5_000

export function DepthLadder() {
  const instrument = useStore((s) => s.selectedInstrument)
  const quote = useStore((s) => (instrument ? s.quotes.get(instrument) : undefined))
  const [depth, setDepth] = useState<Depth | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!instrument) { setDepth(null); return }
    let cancelled = false
    let timer: number | undefined

    const poll = async () => {
      try {
        const d = await api.depth(String(instrument), quote?.exchange ?? 'NSE')
        if (!cancelled) { setDepth(d); setError(null) }
      } catch (e) {
        if (!cancelled) { setDepth(null); setError(String(e)) }
      }
      if (!cancelled) timer = window.setTimeout(() => void poll(), POLL_MS)
    }
    void poll()
    return () => { cancelled = true; if (timer) window.clearTimeout(timer) }
  }, [instrument, quote?.exchange])

  if (!instrument) {
    return (
      <Panel title="Market depth">
        <Empty>Select an instrument</Empty>
      </Panel>
    )
  }

  if (error || !depth) {
    return (
      <Panel title={`Market depth · ${instrument}`}>
        <Empty>{error ? 'Depth is unavailable for this instrument.' : 'Loading…'}</Empty>
      </Panel>
    )
  }

  if (depth.depth_source === 'NONE') {
    return (
      <Panel title={`Market depth · ${depth.symbol}`}>
        <Empty>
          No quote is available for {depth.symbol} from any configured feed, so
          there is no book to show.
        </Empty>
      </Panel>
    )
  }

  const derived = depth.depth_source === 'DERIVED'
  const maxQty = Math.max(
    ...depth.bids.map((b) => b.quantity),
    ...depth.asks.map((a) => a.quantity), 1)

  return (
    <Panel
      title={`Market depth · ${depth.symbol}`}
      noPadding
      actions={
        <span style={{
          fontSize: 9, padding: '1px 6px', borderRadius: 'var(--radius-sm)',
          background: derived ? 'var(--warn-bg)' : 'var(--ok-bg)',
          border: `1px solid ${derived ? 'var(--warn)' : 'var(--ok)'}`,
          color: derived ? 'var(--warn)' : 'var(--ok)',
          letterSpacing: '0.05em', fontWeight: 700,
        }}>
          {derived ? 'CONSTRUCTED' : 'VENUE DEPTH'}
        </span>
      }
    >
      {derived && (
        <div style={{
          margin: 'var(--sp-2)', padding: 'var(--sp-2) var(--sp-3)',
          background: 'var(--warn-bg)', border: '1px solid var(--warn)',
          borderRadius: 'var(--radius-sm)', color: 'var(--warn)',
          fontSize: 9, lineHeight: 1.5,
        }}>
          <b>Top of book is a real quote{depth.quote_source ? ` (${depth.quote_source})` : ''}.</b>{' '}
          The levels beneath it were constructed from the quoted spread and tick
          size — they are not resting exchange orders. Do not size an order from
          these quantities. A depth-subscribed venue feed is required for a real
          ladder.
        </div>
      )}

      {/* Touch summary — the part that IS real on a derived ladder. */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 'var(--sp-2)', padding: 'var(--sp-2) var(--sp-3)',
        borderBottom: '1px solid var(--border)',
      }}>
        <Stat label="Bid" value={fmtPrice(depth.bid)} tone="buy" />
        <Stat label="Ask" value={fmtPrice(depth.ask)} tone="sell" />
        <Stat label="Last" value={fmtPrice(depth.last)} />
        <Stat label="Spread" value={fmtPrice(depth.spread)} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
        <Side rows={depth.bids} side="bid" maxQty={maxQty} derived={derived} />
        <Side rows={depth.asks} side="ask" maxQty={maxQty} derived={derived} />
      </div>

      <div style={{
        display: 'flex', justifyContent: 'space-between',
        padding: 'var(--sp-2) var(--sp-3)',
        borderTop: '1px solid var(--border)',
        fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
      }}>
        <span>
          Total bid{' '}
          <span className="num" style={{ color: derived ? 'var(--text-muted)' : 'var(--buy)' }}>
            {depth.total_bid_quantity?.toLocaleString('en-IN')}
          </span>
        </span>
        <span>
          {derived
            ? 'totals are constructed, not liquidity'
            : `imbalance ${depth.imbalance_pct?.toFixed(1)}%`}
        </span>
        <span>
          Total ask{' '}
          <span className="num" style={{ color: derived ? 'var(--text-muted)' : 'var(--sell)' }}>
            {depth.total_ask_quantity?.toLocaleString('en-IN')}
          </span>
        </span>
      </div>
    </Panel>
  )
}

function Side({
  rows, side, maxQty, derived,
}: {
  rows: DepthRow[]
  side: 'bid' | 'ask'
  maxQty: number
  derived: boolean
}) {
  const color = side === 'bid' ? 'var(--buy)' : 'var(--sell)'
  const bg = side === 'bid' ? 'var(--buy-bg)' : 'var(--sell-bg)'

  return (
    <div style={{
      borderRight: side === 'bid' ? '1px solid var(--border)' : undefined,
    }}>
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr 44px',
        padding: '3px var(--sp-2)', gap: 'var(--sp-1)',
        fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.05em',
        borderBottom: '1px solid var(--border)',
      }}>
        {side === 'bid' ? (
          <><span>Orders</span><span style={{ textAlign: 'right' }}>Qty</span>
            <span style={{ textAlign: 'right' }}>Bid</span></>
        ) : (
          <><span>Ask</span><span style={{ textAlign: 'right' }}>Qty</span>
            <span style={{ textAlign: 'right' }}>Orders</span></>
        )}
      </div>

      {rows.map((r) => {
        const frac = r.quantity / maxQty
        return (
          <div key={`${side}-${r.level}`} style={{
            position: 'relative', height: 22,
            display: 'grid', gridTemplateColumns: '1fr 1fr 60px',
            alignItems: 'center', gap: 'var(--sp-1)',
            padding: '0 var(--sp-2)', fontSize: 'var(--fs-xs)',
          }}>
            {/* The depth bar. On a derived ladder it is drawn hatched rather
                than solid, so a glance can tell constructed shape from real
                resting size without reading the banner again. */}
            <div style={{
              position: 'absolute', top: 2, bottom: 2,
              [side === 'bid' ? 'right' : 'left']: 0,
              width: `${frac * 100}%`,
              background: derived
                ? `repeating-linear-gradient(45deg, ${bg}, ${bg} 3px, transparent 3px, transparent 6px)`
                : bg,
              borderRadius: 2,
              pointerEvents: 'none',
            }} />
            {side === 'bid' ? (
              <>
                <span className="num" style={{ color: 'var(--text-muted)', zIndex: 1 }}>
                  {r.orders}
                </span>
                <span className="num" style={{
                  textAlign: 'right', zIndex: 1,
                  color: derived ? 'var(--text-muted)' : 'var(--text-primary)',
                }}>
                  {r.quantity.toLocaleString('en-IN')}
                </span>
                <span className="num" style={{ textAlign: 'right', color, zIndex: 1, fontWeight: 600 }}>
                  {fmtPrice(r.price)}
                </span>
              </>
            ) : (
              <>
                <span className="num" style={{ color, zIndex: 1, fontWeight: 600 }}>
                  {fmtPrice(r.price)}
                </span>
                <span className="num" style={{
                  textAlign: 'right', zIndex: 1,
                  color: derived ? 'var(--text-muted)' : 'var(--text-primary)',
                }}>
                  {r.quantity.toLocaleString('en-IN')}
                </span>
                <span className="num" style={{ textAlign: 'right', color: 'var(--text-muted)', zIndex: 1 }}>
                  {r.orders}
                </span>
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

function Stat({ label, value, tone }: {
  label: string; value: string; tone?: 'buy' | 'sell'
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <span style={{
        fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        {label}
      </span>
      <span className="num" style={{
        fontSize: 'var(--fs-sm)', fontWeight: 600,
        color: tone === 'buy' ? 'var(--buy)' : tone === 'sell' ? 'var(--sell)' : undefined,
      }}>
        {value}
      </span>
    </div>
  )
}
